import { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, getApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Bell, Users, FileSignature, FileText, MessageSquare, BarChart3, ShieldCheck } from "lucide-react";

import StatCard from "@/components/admin/StatCard";
import AppRow from "@/components/admin/AppRow";
import SignaturesTab from "@/components/admin/SignaturesTab";
import FilesTab from "@/components/admin/FilesTab";
import MembersTab from "@/components/admin/MembersTab";
import ContactInbox from "@/components/admin/ContactInbox";
import NotificationsTab, { NOTIF_KINDS } from "@/components/admin/NotificationsTab";
import AdminMessages from "@/components/admin/AdminMessages";
import AdminAnalytics from "@/components/admin/AdminAnalytics";
import AdminSecurity from "@/components/admin/AdminSecurity";
import RecentSignups from "@/components/admin/RecentSignups";

export default function AdminPortal() {
  const [activeTab, setActiveTab] = useState("applications");
  const [overview, setOverview] = useState({});
  const [apps, setApps] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [files, setFiles] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [notifFilter, setNotifFilter] = useState("all");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const prevUnreadRef = useRef(0);

  const refresh = async (silent = false) => {
    try {
      const [o, a, s, f, n, m, c] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/applications"),
        api.get("/esign/all"),
        api.get("/files/all"),
        api.get("/admin/notifications"),
        api.get("/admin/members"),
        api.get("/admin/contact-messages"),
      ]);
      // Detect NEW unread alerts during background polling and surface a toast.
      const newUnread = o.data.unread_notifications ?? 0;
      if (silent && newUnread > prevUnreadRef.current) {
        const delta = newUnread - prevUnreadRef.current;
        const latest = n.data?.find((x) => !x.read);
        toast.success(
          delta === 1 ? latest?.title || "New alert" : `${delta} new alerts`,
          { description: latest?.body?.slice(0, 100) }
        );
      }
      prevUnreadRef.current = newUnread;
      setOverview(o.data);
      setApps(a.data);
      setSignatures(s.data);
      setFiles(f.data);
      setNotifications(n.data);
      setMembers(m.data);
      setMessages(c.data);
      setLastRefreshed(new Date());
    } catch (e) { if (!silent) toast.error(getApiError(e)); }
  };

  // Auto-refresh every 30s while admin is open. Pauses when tab is hidden.
  useEffect(() => {
    if (!autoRefresh) return;
    const tick = () => { if (!document.hidden) refresh(true); };
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  const notifCounts = useMemo(() => {
    const counts = { all: notifications.length };
    NOTIF_KINDS.forEach((k) => {
      if (k.key !== "all") counts[k.key] = notifications.filter((n) => n.kind === k.key).length;
    });
    return counts;
  }, [notifications]);

  const filteredNotifications = useMemo(() => (
    notifFilter === "all" ? notifications : notifications.filter((n) => n.kind === notifFilter)
  ), [notifications, notifFilter]);

  useEffect(() => { refresh(); }, []);

  const markAllRead = async () => {
    try { await api.post("/admin/notifications/read-all"); refresh(); }
    catch (e) { toast.error(getApiError(e)); }
  };

  const unreadMsgs = overview.unread_messages ?? 0;

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-portal">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-8">
            <div className="overline mb-4">§ Trustee Console · MMXXVI</div>
            <h1 className="font-serif font-light text-5xl md:text-6xl leading-[1.02] tracking-tight text-balance">
              Guild Operations.
            </h1>
          </div>
          <div className="lg:col-span-4 lg:text-right text-sm text-stone-500 font-light">
            {lastRefreshed && (
              <span className="caption">
                Last refreshed · {lastRefreshed.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 border-t border-l border-stone-200" data-testid="admin-overview-stats">
          <StatCard label="Members" value={overview.members ?? 0} icon={Users} />
          <StatCard label="Applications" value={overview.applications ?? 0} icon={FileText} />
          <StatCard label="Signatures" value={overview.signatures ?? 0} icon={FileSignature} />
          <StatCard label="Documents" value={overview.files ?? 0} icon={FileText} />
          <StatCard label="Paid Tx" value={overview.paid_transactions ?? 0} icon={FileText} />
          <StatCard label="Unread Msgs" value={unreadMsgs} icon={MessageSquare} highlight />
          <StatCard label="Unread Alerts" value={overview.unread_notifications ?? 0} icon={Bell} highlight />
        </div>

        {/* Recent Signups — always visible, above tabs */}
        <RecentSignups members={members} onViewAll={() => setActiveTab("members")} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-12">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList data-testid="admin-tabs" className="w-max">
              <TabsTrigger value="applications" data-testid="admin-tab-applications">Applications</TabsTrigger>
              <TabsTrigger value="threads" data-testid="admin-tab-threads">
                Messages {unreadMsgs > 0 && <span className="ml-1 text-[color:var(--fire-red)]">({unreadMsgs})</span>}
              </TabsTrigger>
              <TabsTrigger value="signatures" data-testid="admin-tab-signatures">Signatures</TabsTrigger>
              <TabsTrigger value="files" data-testid="admin-tab-files">Documents</TabsTrigger>
              <TabsTrigger value="members" data-testid="admin-tab-members">Members</TabsTrigger>
              <TabsTrigger value="messages" data-testid="admin-tab-messages">Contact</TabsTrigger>
              <TabsTrigger value="notifications" data-testid="admin-tab-notifications">
                Alerts {overview.unread_notifications > 0 && <span className="ml-1 text-[color:var(--fire-red)]">({overview.unread_notifications})</span>}
              </TabsTrigger>
              <TabsTrigger value="analytics" data-testid="admin-tab-analytics">
                <BarChart3 className="h-3.5 w-3.5 mr-1" />Traffic
              </TabsTrigger>
              <TabsTrigger value="security" data-testid="admin-tab-security">
                <ShieldCheck className="h-3.5 w-3.5 mr-1" />Security
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="applications">
            <div className="space-y-4 mt-4">
              {apps.map((a) => <AppRow key={a.application_id} app={a} onUpdate={refresh} />)}
              {apps.length === 0 && <p className="text-sm text-[color:var(--text-secondary)]">No applications yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="threads">
            <AdminMessages />
          </TabsContent>

          <TabsContent value="signatures"><SignaturesTab signatures={signatures} /></TabsContent>
          <TabsContent value="files"><FilesTab files={files} /></TabsContent>
          <TabsContent value="members"><MembersTab members={members} /></TabsContent>
          <TabsContent value="messages"><ContactInbox messages={messages} /></TabsContent>

          <TabsContent value="notifications">
            <NotificationsTab
              notifications={filteredNotifications}
              counts={notifCounts}
              filter={notifFilter}
              setFilter={setNotifFilter}
              autoRefresh={autoRefresh}
              setAutoRefresh={setAutoRefresh}
              lastRefreshed={lastRefreshed}
              onRefresh={() => refresh(false)}
              onMarkAllRead={markAllRead}
            />
          </TabsContent>

          <TabsContent value="analytics"><AdminAnalytics /></TabsContent>
          <TabsContent value="security"><AdminSecurity /></TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}
